import React, { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CampaignOffersQueryKeyEnum } from 'views/Invest/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Table from 'components/Table';
import useMutate from 'views/Trade/hooks/useMutate';
import { buildTableRow } from 'views/Portfolio/helpers';
import CheckBox from 'components/CheckBox';
import { useSearchParams } from 'react-router-dom';
import { GET_MISCELLANEOUS } from '../../../App/graphql/getMiscellaneous';
import {
  LanguageIconCanada,
  LanguageIconChina,
  LanguageIconGB,
  LanguageIconHongKong,
  LanguageIconJapan,
  LanguageIconSingapore,
  LanguageIconUSA,
  SuccessTickIcon,
} from '../../../assets/icons';
import { Button, Dropdown } from '../../../components';
import { DisplayField, DisplayFieldType, DisplaySection } from '../../../components/DisplayForms';
import DisplayForm from '../../../components/DisplayForms/DisplayForm';
import { DropdownItem } from '../../../components/Dropdown';
import { logError } from '../../../components/LogError';
import { ViewStateType } from '../../../components/ProductTemplates/types';
import { AppContext } from '../../../context/ContextProvider';
import { getFieldAttributes, passwordChangeValidationTests } from '../../../helpers';
import { AppEventTypes, MiscellaneousType, SettingsEventTypes, UserSettings } from '../../../types/AppType';
import { NavigationPath, RefreshRegion } from '../../../types/DomainTypes';
import { buildDisplayText, capitalizeFirstLetter, sortItems, toInternalId } from '../../../utils';
import { useExecuteMutation } from '../../hooks/useExecuteMutation';
import { useExecuteQuery } from '../../hooks/useExecuteQuery';
import { FeedbackTemplate } from '../../shared/FeedbackTemplate';
import { CHANGE_PASSWORD_MUTATION } from '../graphql/changePasswodMutation';
import { UPDATE_USER_PREFERENCES } from '../graphql/updateUserPreferences';
import {
  AccountViewType,
  AutoTradeModelEnum,
  IAutoTradeModelProps,
  OpenSlideoutFnType,
  SlideOutPanelViews,
} from '../types';
import { getNotificationPreferencesApi, NotificationPreferencesType } from '../services/getNotificationPreferencesApi';
import { autoTradeModelConfig, InitialiseNotificationTable } from '../helper';
import { setNotificationPreferencesApi } from '../services/setNotificationPreferencesApi';
import AutoTradeModel from './AutoTradeModel';
import { GET_USER_PREFERENCES } from '../graphql/getUserPreferences';
import { PortalClientPreferencesType } from './ManagementFees/types';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeToggleWithIcons } from '../../../components/ThemeToggle';

const ddlConfig = {
  itemsWrapperClassName: 'min-w-[300px] overflow-x-hidden',
  itemWrapperStyle: { width: '100%' },
  containerClassName: 'w-full flex-1',
  itemsContainerClassName: 'h-[300px] overflow-y-auto w-full',
  itemClassName: 'text-base flex flex-1',
  className:
    'flex-1 text-sm sm:text-14 text-gray-800 dark:text-gray-200 whitespace-nowrap p-0 justify-start border-b border-gray-300 dark:border-slate-600',
};

type MiscItem = Pick<DropdownItem, 'id' | 'value' | 'text'> & { symbol?: string };
const feedbackViewContainerClassname =
  'flex flex-col flex-1 bg-gradient-to-b  from-gray-100 to-gray-500  pb-5 px-3 w-full relative overflow-x-hidden sm:w-[390px]';
const languageIconMap = new Map<string, ReactNode>([
  ['en-GB', <LanguageIconGB />],
  ['zh-CN', <LanguageIconChina />],
  ['en-CA', <LanguageIconCanada />],
  ['en-HK', <LanguageIconHongKong />],
  ['en-US', <LanguageIconUSA />],
  ['ja-JP', <LanguageIconJapan />],
  ['en-SG', <LanguageIconSingapore />],
]);

const getCurrencyDropDownOptions = (source: MiscItem[]): DropdownItem[] => {
  return sortItems([...(source || [])], true, 'text').map((option) => {
    const { id, text, value, symbol } = option;
    const displayText = capitalizeFirstLetter(text!);
    return {
      id: toInternalId(id),
      value: value || id,
      text: displayText,
      content: (
        <div className="flex text-14 space-x-3">
          <span>{symbol}</span>
          <span>{displayText}</span>
        </div>
      ),
    };
  });
};

const getLanguageDropDownOptions = (source: MiscItem[]): DropdownItem[] => {
  return sortItems([...(source || [])], true, 'text').map((option) => {
    const { id, text, value, symbol } = option;
    return {
      id: toInternalId(id),
      value: value || id,
      text: `${text} (${symbol})`,
      content: (
        <div className="flex text-14 items-center justify-between w-full">
          <div className="flex items-center">
            <span>{languageIconMap.get(id)}</span>
            <span>{text}</span>
          </div>

          <span>{symbol}</span>
        </div>
      ),
    };
  });
};

enum DisplayTextKeys {
  LANGUAGE_TEXT = 'language_text',
  CURRENCY_TEXT = 'currency_text',
  EMAIL_TEXT = 'email-text',
  CHANGE_PASSWORD_TEXT = 'change_password_text',
  EDIT_TEXT = 'edit_text',
  CANCEL_TEXT = 'cancel_text',
  SAVE_TEXT = 'save_text',
  EMAIL_AUTHENTICATION_TEXT = 'email_authentication_text',
  TITLE_TEXT = 'preferences_text',
  SAME_AS_LOGIN_TEXT = 'same_as_login',
  CHANGE_PASSWORD = 'change_password',

  CURRENT_PASSWORD_TEXT = 'current_password_text',
  NEW_PASSWORD_TEXT = 'new_password_text',
  CONFIRM_NEW_PASSWORD_TEXT = 'confirm_new-password_text',

  PASSWORD_INSTRUCTIONS = 'password_instructions',

  PASSWORD_CHANGED_SUCCESS_TEXT = 'password_changed_success_text',
  PASSWORD_CHANGED_ERROR_TEXT = 'password_changed_error_text',
  NOTIFICATION_PREFERENCES_TEXT = 'notification_preferences_text',

  AUTO_TRADE = 'auto_trade_text',
  THEME_TEXT = 'theme_text',
  APPEARANCE_TEXT = 'appearance_text',
}

enum ModelKeys {
  LANGUAGE = 'language',
  CURRENCY = 'currency',
  EMAIL = 'email',
  SAME_AS_LOGIN = 'sameAsLogin',

  CURRENT_PASSWORD = 'currentPassword',
  NEW_PASSWORD = 'newPassword',
  CONFIRM_NEW_PASSWORD = 'confirmNewPassword',

  NOTIFICATION_PREFERENCES = 'notification_preferences',
  AUTO_TRADE = 'auto_trade',
}
interface UserPreference {
  portalUpdatePreferences: UserSettings;
}

interface ModelType {
  [ModelKeys.LANGUAGE]: string;
  [ModelKeys.CURRENCY]: string;
  [ModelKeys.EMAIL]: string;
  [ModelKeys.SAME_AS_LOGIN]: boolean;
  [ModelKeys.CURRENT_PASSWORD]: string;
  [ModelKeys.NEW_PASSWORD]: string;
  [ModelKeys.CONFIRM_NEW_PASSWORD]: string;
  [ModelKeys.NOTIFICATION_PREFERENCES]: string;
  [ModelKeys.AUTO_TRADE]: string;
}

const defaultModel: ModelType = {
  [ModelKeys.LANGUAGE]: '',
  [ModelKeys.CURRENCY]: '',
  [ModelKeys.EMAIL]: '',
  [ModelKeys.SAME_AS_LOGIN]: false,
  [ModelKeys.CURRENT_PASSWORD]: '',
  [ModelKeys.NEW_PASSWORD]: '',
  [ModelKeys.CONFIRM_NEW_PASSWORD]: '',
  [ModelKeys.NOTIFICATION_PREFERENCES]: '',
  [ModelKeys.AUTO_TRADE]: '',
};

interface SettingProps {
  openSlideout: OpenSlideoutFnType;
  onClose: (nextView?: AccountViewType) => void;
}
const Settings: FC<SettingProps> = ({ openSlideout, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { accountNotificationSettings } = useFlags();
  const [searchParams] = useSearchParams();
  const navToParam = searchParams.get('navTo');

  const targetRef = useRef<HTMLDivElement | null>(null);

  const {
    state: {
      settings: { email, currency, language },
    },
    dispatch,
    onLanguageChange,
  } = useContext(AppContext);

  const { theme, isDark } = useTheme();

  const { data: notificationPreferenceData } = useQuery({
    enabled: accountNotificationSettings,
    queryFn: getNotificationPreferencesApi,
    queryKey: ['getNotificationPreferencesApi'],
  });
  //  console.log('notificationPreferenceData ', notificationPreferenceData);
  const {
    mutateAsync: saveNotificationPreferences,
    isLoading: saveNotificationPreferencesIsLoading,
    error: saveNotificationPreferencesError,
  } = useMutate({
    mutationFn: setNotificationPreferencesApi,
  });

  const {
    data: userPreferencesData,
    loading: loadingUserPreferences,
    refetch: refetchUserPreferencesData,
  } = useExecuteQuery('PortalClientPreferences', GET_USER_PREFERENCES, { fetchPolicy: 'network-only' });

  const {
    executor: savePreference,
    error: preferenceError,
    loading: savingPreferences,
    data: userPreferences,
  } = useExecuteMutation(UPDATE_USER_PREFERENCES);

  const [saveAutoTradePreference] = useMutation(UPDATE_USER_PREFERENCES);

  const { results: misc } = useExecuteQuery('miscellaneous', GET_MISCELLANEOUS);
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:settings', t), [t]);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferencesType[]>([]);

  const [autoTradeConfig, setAutoTradeConfig] = useState<IAutoTradeModelProps>({
    isOpen: false,
    autoTradeEnabled: false,
    selectedId: AutoTradeModelEnum.NONE,
  });

  const [isLoading, setIsLoading] = useState(false);
  const onSaveNotificationPreferences = useCallback(async () => {
    const body = notificationPreferences.map((x) => ({
      categoryId: x.categoryId,
      email: x.email,
    }));
    setIsLoading(true);
    await saveNotificationPreferences({ portalPreferences: body });
    setTimeout(async () => {
      await queryClient.resetQueries({ queryKey: ['getNotificationPreferencesApi'] });
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }, 1500);
  }, [notificationPreferences, saveNotificationPreferences, queryClient, setIsLoading]);

  const onCheckNotificationPreferences = useCallback(
    (categoryId: string, emailUser: boolean) => {
      const preferences = notificationPreferences.map((x) => {
        return x.categoryId === categoryId ? { ...x, email: emailUser } : x;
      });
      setNotificationPreferences(preferences);
    },
    [notificationPreferences, setNotificationPreferences],
  );

  useEffect(() => {
    if (loadingUserPreferences) return;
    const preferencesData = userPreferencesData as PortalClientPreferencesType;
    setAutoTradeConfig((prev) => ({
      ...prev,
      autoTradeEnabled: preferencesData.portalClientPreferences.autoTrade,
      modelConfig: autoTradeModelConfig({
        isLoading: autoTradeConfig.isLoading,
        t,
        onClose: () => {
          setAutoTradeConfig((previous) => ({
            ...previous,
            selectedId: AutoTradeModelEnum.NONE,
            isOpen: false,
          }));
        },
        toggleAutoTrade: async (bool: boolean, selectedId: AutoTradeModelEnum) => {
          try {
            setAutoTradeConfig((previous) => ({
              ...previous,
              isLoading: true,
            }));

            await saveAutoTradePreference({
              variables: {
                request: {
                  autoTrade: bool,
                },
              },
            });

            saveNotificationPreferences({
              portalPreferences: [
                {
                  categoryId: 'D47B9520-7108-4762-B59D-A7B14B06365A',
                  email: !bool,
                },
              ],
            });

            setTimeout(async () => {
              await queryClient.resetQueries({ queryKey: ['getNotificationPreferencesApi'] });

              refetchUserPreferencesData();

              setAutoTradeConfig((previous) => ({
                ...previous,
                autoTradeEnabled: bool,
                selectedId,
                isLoading: false,
              }));
            }, 1500);
          } catch (error) {
            setAutoTradeConfig((previous) => ({
              ...previous,
              autoTradeEnabled: prev.autoTradeEnabled,
              selectedId: AutoTradeModelEnum.ERROR,
              isLoading: false,
            }));
          }
        },
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userPreferencesData,
    loadingUserPreferences,
    saveAutoTradePreference,
    refetchUserPreferencesData,
    autoTradeConfig.isLoading,
  ]);

  useEffect(() => {
    if (notificationPreferenceData?.preferences)
      setNotificationPreferences(
        (notificationPreferenceData.preferences || []).map((x) => {
          return { ...x, email: x.email === null ? true : x.email };
        }),
      );
  }, [notificationPreferenceData?.preferences, setNotificationPreferences]);

  const { notificationColumns } = useMemo(() => {
    return {
      ...InitialiseNotificationTable(t, onCheckNotificationPreferences),
    };
  }, [t, onCheckNotificationPreferences]);

  const notificationPreferencesRows = useMemo(() => {
    const rows = (notificationPreferences || []).map((data) => {
      return {
        ...buildTableRow(
          {
            ...data,
          },
          notificationColumns,
          '',
          'border-none',
        ),
      };
    });

    return rows;
  }, [notificationColumns, notificationPreferences]);

  const languageOptions = useMemo(
    () =>
      // hide japanese language option for now
      getLanguageDropDownOptions((misc as MiscellaneousType).languages as MiscItem[]).filter(
        (l) => l.value !== 'ja-JP',
      ),
    [misc],
  );
  const currencyOptions = useMemo(
    () => getCurrencyDropDownOptions((misc as MiscellaneousType).currencies as MiscItem[]),
    [misc],
  );
  const containerRef = useRef<HTMLDivElement | null>();
  const [processingPwdChange, setProcessingPasswordChange] = useState(false);

  const [model, setModel] = useState<ModelType>({
    ...defaultModel,
    [ModelKeys.EMAIL]: email,
    [ModelKeys.CURRENCY]: currency,
    [ModelKeys.LANGUAGE]: language,
  });
  const {
    executor: passwordProcessor,
    error: passwordUpdateError,
    loading: loadingPasswordUpdates,
    data: pwdData,
  } = useExecuteMutation(CHANGE_PASSWORD_MUTATION);

  const closeOnSuccess = () => {
    dispatch({
      type: AppEventTypes.UPDATE_STATE,
      payload: {
        refresh: [RefreshRegion.HEADER, NavigationPath.PORTFOLIO, NavigationPath.MY_CELLAR, NavigationPath.INVEST],
      },
    });
    onClose();
  };

  const successView = useMemo(() => {
    const { [ModelKeys.CURRENCY]: newCurrency } = model;
    const isCurrencyChanged = newCurrency !== currency;

    return (
      <div className={feedbackViewContainerClassname}>
        <div className="w-full h-full rounded-md overflow-y-auto flex flex-col">
          <FeedbackTemplate
            onClose={closeOnSuccess}
            templateConfig={
              isCurrencyChanged
                ? {
                    translationKey: 'account:currencyChangeSuccess',
                  }
                : {}
            }
            viewState={ViewStateType.SUCCESS}
            onCTA={closeOnSuccess}
          />
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, model]);

  const ErrorView = useMemo(() => {
    return (
      <div className={feedbackViewContainerClassname}>
        <div className="w-full h-full rounded-md overflow-y-auto flex flex-col">
          <FeedbackTemplate
            onClose={onClose}
            templateConfig={{}}
            viewState={ViewStateType.ERROR_RESULT}
            onCTA={() => onClose()}
          />
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangePassword = () => {
    const {
      [ModelKeys.NEW_PASSWORD]: passwordNew,
      [ModelKeys.CURRENT_PASSWORD]: passwordCurrent,
      [ModelKeys.CONFIRM_NEW_PASSWORD]: passwordConfirm,
    } = model;
    setProcessingPasswordChange(true);
    passwordProcessor({
      changePasswordInput: {
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID!,
        passwordCurrent,
        passwordNew,
        passwordConfirm,
      },
    });
  };

  // const passwordChangeValidationTests = (value: string): Record<string, { isValid: boolean; text: string }> => {
  //   const keys = t(`account:settings.${DisplayTextKeys.PASSWORD_INSTRUCTIONS}`);
  //   const validations = keys.split(';').reduce((result, key) => {
  //     return {
  //       ...result,
  //       [key]: {
  //         isValid: validatePasswordChange(key as PasswordChangeValidationKeys, value),
  //         text: t(`account:settings.${key}`),
  //       },
  //     };
  //   }, {});

  //   return validations;
  // };

  const updateModel = (key: string, value: unknown) => {
    setModel({ ...model, [key]: value });
  };
  const onFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) =>
    updateModel(field?.modelKey || '', e.target.value);

  const onSavePrefences = () => {
    queryClient.resetQueries([String(CampaignOffersQueryKeyEnum.fetchCampaignOffers)]);
    const { [ModelKeys.CURRENCY]: newCurrency, [ModelKeys.LANGUAGE]: newLanguage } = model;
    savePreference({
      request: {
        currency: newCurrency,
        language: newLanguage,
      },
    });
  };

  const sections: DisplaySection[] = useMemo(() => {
    const preferences: DisplayField[] = [
      {
        modelKey: ModelKeys.LANGUAGE,
        options: languageOptions,
      },
      {
        modelKey: ModelKeys.CURRENCY,
        options: currencyOptions,
      },
    ].map((preference) => ({
      ...getFieldAttributes(
        preference.modelKey,
        preference.modelKey,
        displayText[`${preference.modelKey}_TEXT`.toUpperCase()],
      ),
      type: DisplayFieldType.CUSTOM,
      customTemplate: () => {
        return (
          <div className="flex flex-col w-full">
            <span className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">
              {displayText[`${preference.modelKey}_text`]}
            </span>
            <Dropdown
              placeholder="Select item"
              value={model[preference.modelKey] as string}
              valueTemplate={
                <div className="flex w-[95%]">
                  <span className="truncate block text-gray-800 dark:text-gray-200">
                    {preference.options.find((x) => x.value === model[preference.modelKey])?.text}
                  </span>
                </div>
              }
              onItemSelect={(item: DropdownItem) => updateModel(preference.modelKey, item.value)}
              items={preference.options}
              {...ddlConfig}
            />
          </div>
        );
      },
    }));
    const isButtonDisabled =
      savingPreferences || (currency === model[ModelKeys.CURRENCY] && language === model[ModelKeys.LANGUAGE]);
    return [
      {
        className: 'w-full flex gap-5 ',
        fields: [...preferences],
        footer: () => (
          <div className="flex flex-col mt-4">
            <Button
              isProcessing={savingPreferences}
              isDisable={isButtonDisabled}
              className={`w-fit rounded-xl px-6 py-2.5 font-medium transition-all ${
                isButtonDisabled
                  ? 'bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
              }`}
              onClick={onSavePrefences}
              props={{
                name: displayText[DisplayTextKeys.SAVE_TEXT],
              }}
            >
              {displayText[DisplayTextKeys.SAVE_TEXT]}
            </Button>
          </div>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, savePreference]);

  const changePasswordSections: DisplaySection[] = useMemo(() => {
    if (passwordUpdateError) {
      if (processingPwdChange) setProcessingPasswordChange(false);
      logError(passwordUpdateError);
      openSlideout(SlideOutPanelViews.CUSTOM, { title: t`common:error`, customTemplate: () => ErrorView });
    }

    const changePasswordFields: DisplayField[] = (
      ['CURRENT_PASSWORD', 'NEW_PASSWORD', 'CONFIRM_NEW_PASSWORD'] as const
    ).map((field) => ({
      ...getFieldAttributes(
        field,
        ModelKeys[`${field}` as const],
        displayText[DisplayTextKeys[`${field}_TEXT` as const]],
      ),
      disabled: loadingPasswordUpdates,

      type: DisplayFieldType.PASSWORD,
      inputProps: { showClearButton: true },
      onChange: onFieldUpdate,
    }));

    let validations = passwordChangeValidationTests(
      model[ModelKeys.NEW_PASSWORD],
      t,
      `account:settings.${DisplayTextKeys.PASSWORD_INSTRUCTIONS}` as const,
    );

    validations = {
      ...validations,
      sameTest: {
        isValid:
          model[ModelKeys.NEW_PASSWORD].length > 0 &&
          model[ModelKeys.NEW_PASSWORD] === model[ModelKeys.CONFIRM_NEW_PASSWORD],
        text: validations.sameTest.text,
      },
    };

    const contraintsTests = Object.keys(validations).map((x) => validations[x].isValid);
    const passwordContraints = {
      ...getFieldAttributes('passwordContraints', 'passwordContraints', ''),
      type: DisplayFieldType.CUSTOM,
      customTemplate: () => {
        return (
          <ul className="list-none space-y-1 mt-2 p-4 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
            {Object.keys(validations).map((key, index) => {
              const test = validations[key];
              return (
                <li key={`pwd-info-${index}`} className="text-sm">
                  <div className="flex items-center">
                    <div className="mr-2">
                      {test.isValid ? (
                        <SuccessTickIcon className="text-green-500" />
                      ) : (
                        <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
                      )}
                    </div>
                    <span
                      className={
                        test.isValid ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                      }
                    >
                      {`${test.text}`}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        );
      },
    };

    const canChange =
      !contraintsTests.includes(false) && !loadingPasswordUpdates && model[ModelKeys.CURRENT_PASSWORD].length > 7;

    return [
      {
        className: 'w-full gap-5',
        fields: [...changePasswordFields, passwordContraints],
        footer: () => (
          <div className="flex flex-col mt-4">
            <Button
              isProcessing={loadingPasswordUpdates}
              isDisable={!canChange}
              className={`w-fit rounded-xl px-6 py-2.5 font-medium transition-all ${
                !canChange
                  ? 'bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
              }`}
              onClick={onChangePassword}
              props={{
                name: displayText[DisplayTextKeys.CHANGE_PASSWORD_TEXT],
              }}
            >
              {displayText[DisplayTextKeys.CHANGE_PASSWORD_TEXT]}
            </Button>
          </div>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, loadingPasswordUpdates, passwordUpdateError]);

  const autoTradePreferenceSection: DisplaySection[] = useMemo(() => {
    const preferences: DisplayField[] = [
      {
        modelKey: ModelKeys.AUTO_TRADE,
      },
    ].map((preference) => ({
      ...getFieldAttributes(
        preference.modelKey,
        preference.modelKey,
        displayText[`${preference.modelKey}_TEXT`.toUpperCase()],
      ),
      type: DisplayFieldType.CUSTOM,
      customTemplate: () => {
        if (loadingUserPreferences) return null;
        return (
          <div className="flex flex-col w-full">
            <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange/20 to-orange/10 dark:from-teal-500/20 dark:to-teal-500/10 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-orange dark:text-teal-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('common:autoTradeSettings.autoTrade')}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {autoTradeConfig.autoTradeEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
              <CheckBox
                inputClassName="max-h-[18px] max-w-[18px] min-h-[18px] min-w-[18px]"
                isChecked={autoTradeConfig.autoTradeEnabled}
                id="autoTradeId"
                onChange={() => {
                  if (!autoTradeConfig.autoTradeEnabled) {
                    setAutoTradeConfig((prev) => ({
                      ...prev,
                      selectedId: AutoTradeModelEnum.CONFIRM_ENABLE,
                      isOpen: true,
                    }));
                  } else {
                    setAutoTradeConfig((prev) => ({
                      ...prev,
                      selectedId: AutoTradeModelEnum.CONFIRM_DISABLE,
                      isOpen: true,
                    }));
                  }
                }}
              />
            </div>
            <AutoTradeModel
              isDisabled={autoTradeConfig.isLoading}
              config={autoTradeConfig?.modelConfig?.find((x) => x.id === autoTradeConfig.selectedId) ?? {}}
              open={autoTradeConfig.isOpen}
              onClose={() => {
                setAutoTradeConfig((prev) => ({
                  ...prev,
                  selectedId: AutoTradeModelEnum.NONE,
                  isOpen: false,
                }));
              }}
            />
          </div>
        );
      },
    }));

    return [
      {
        className: 'w-full flex gap-5 ',
        fields: [...preferences],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTradeConfig, t, loadingUserPreferences]);

  const notificationPreferenceSection: DisplaySection[] = useMemo(() => {
    const preferences: DisplayField[] = [
      {
        modelKey: ModelKeys.NOTIFICATION_PREFERENCES,
      },
    ].map((preference) => ({
      ...getFieldAttributes(
        preference.modelKey,
        preference.modelKey,
        displayText[`${preference.modelKey}_TEXT`.toUpperCase()],
      ),
      type: DisplayFieldType.CUSTOM,
      customTemplate: () => {
        return (
          <div className="flex flex-col w-full bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <Table
              className="!table-fixed"
              theadClassName="bg-gray-50 dark:bg-slate-700/50"
              columns={notificationColumns}
              rows={notificationPreferencesRows}
            />
          </div>
        );
      },
    }));
    const isButtonDisabled =
      !notificationPreferences.length ||
      JSON.stringify(notificationPreferences) ===
        JSON.stringify(
          (notificationPreferenceData?.preferences || []).map((x) => {
            return { ...x, email: x.email === null ? true : x.email };
          }),
        );

    return [
      {
        className: 'w-full flex gap-5 ',
        fields: [...preferences],
        footer: () => (
          <div className="flex flex-col mt-4">
            {saveNotificationPreferencesError ? (
              <span className="text-red-500 dark:text-red-400 text-sm p-2">
                {t('account:settings.notification_preferences_error')}
              </span>
            ) : null}
            <Button
              isProcessing={isLoading}
              isDisable={isButtonDisabled || autoTradeConfig.isLoading}
              className={`w-fit rounded-xl px-6 py-2.5 font-medium transition-all ${
                isButtonDisabled || autoTradeConfig.isLoading
                  ? 'bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
              }`}
              onClick={onSaveNotificationPreferences}
              props={{
                name: displayText[DisplayTextKeys.SAVE_TEXT],
              }}
            >
              {displayText[DisplayTextKeys.SAVE_TEXT]}
            </Button>
          </div>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model,
    onSaveNotificationPreferences,
    notificationPreferences,
    notificationPreferenceData,
    saveNotificationPreferencesIsLoading,
    saveNotificationPreferencesError,
    isLoading,
    autoTradeConfig.isLoading,
    t,
  ]);

  useEffect(() => {
    if (processingPwdChange && !loadingPasswordUpdates) {
      if (
        pwdData &&
        (pwdData as { authChangePassword: { wasPasswordChanged: boolean } }).authChangePassword.wasPasswordChanged
      )
        openSlideout(SlideOutPanelViews.CUSTOM, { title: t`common:success`, customTemplate: () => successView });
      else {
        openSlideout(SlideOutPanelViews.CUSTOM, { title: t`common:error`, customTemplate: () => ErrorView });
      }

      setProcessingPasswordChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPasswordUpdates]);

  useEffect(() => {
    if (!savingPreferences && !!userPreferences) {
      if (preferenceError)
        openSlideout(SlideOutPanelViews.CUSTOM, { title: t`common:error`, customTemplate: () => ErrorView });
      else {
        openSlideout(SlideOutPanelViews.CUSTOM, { title: t`common:success`, customTemplate: () => successView });
        const { currency: newCurrency, language: newLanguage } = (userPreferences as UserPreference)
          .portalUpdatePreferences as UserSettings;
        setModel({ ...model, [ModelKeys.CURRENCY]: newCurrency, [ModelKeys.LANGUAGE]: newLanguage });
        if (newCurrency !== currency) {
          queryClient.resetQueries(['fetchPortfolioBalances']);
          queryClient.resetQueries(['fetchCashBalance']);
          dispatch({
            type: SettingsEventTypes.UPDATE_SETTINGS,
            payload: { currency: newCurrency },
          });
        }

        if (newLanguage !== language && onLanguageChange) onLanguageChange(newLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences]);

  useEffect(() => {
    if (navToParam?.includes('auto-trade') && containerRef.current && targetRef.current) {
      const container = containerRef.current;
      const target = targetRef.current;

      const scrollOffset = target.offsetTop - container.offsetTop;

      container.scrollTo({
        top: scrollOffset,
        behavior: 'smooth',
      });
      return;
    }

    if (containerRef.current) containerRef.current.scrollTo(0, 0);
  }, [navToParam]);

  if (preferenceError) {
    logError(preferenceError);
  }

  return (
    <div
      className="flex flex-col py-6 px-8 sm:px-10 lg:px-12 w-full overflow-y-auto bg-gray-50 dark:bg-slate-900 min-h-full transition-colors"
      ref={(r) => (containerRef.current = r)}
    >
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayText[DisplayTextKeys.TITLE_TEXT]}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your account preferences and security settings
        </p>
      </div>

      {/* Language & Currency Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 mb-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/10 dark:from-blue-400/20 dark:to-blue-500/10 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-blue-600 dark:text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Regional Settings</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Language and currency preferences</p>
          </div>
        </div>
        <DisplayForm
          sectionsContainerClassName="gap-5"
          titleContainerClassName="hidden"
          sections={sections}
          model={{ ...model, modelType: 'Settings' }}
        />
      </div>

      {/* Theme/Appearance Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 mb-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-purple-600/10 dark:from-purple-400/20 dark:to-purple-500/10 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-purple-600 dark:text-purple-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('account:settings.appearance_text') || 'Appearance'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Customize how the app looks</p>
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
          <div>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {t('account:settings.theme_text') || 'Theme'}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isDark ? 'Dark mode is active' : 'Light mode is active'}
            </p>
          </div>
          <ThemeToggleWithIcons />
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 mb-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/10 dark:from-red-400/20 dark:to-red-500/10 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {displayText[DisplayTextKeys.CHANGE_PASSWORD]}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Update your account password</p>
          </div>
        </div>
        <DisplayForm
          titleContainerClassName="hidden"
          sectionsContainerClassName=""
          sections={changePasswordSections}
          model={{ ...model, modelType: displayText[DisplayTextKeys.CHANGE_PASSWORD] }}
        />
      </div>

      {/* Auto Trade Section */}
      <div ref={targetRef}>
        {accountNotificationSettings && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 mb-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange/20 to-orange/10 dark:from-teal-500/20 dark:to-teal-500/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-orange dark:text-teal-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {displayText[DisplayTextKeys.AUTO_TRADE]}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Automated trading preferences</p>
              </div>
            </div>
            <DisplayForm
              titleContainerClassName="hidden"
              sectionsContainerClassName=""
              sections={autoTradePreferenceSection}
              model={{ ...model, modelType: displayText[DisplayTextKeys.AUTO_TRADE] }}
            />
          </div>
        )}
      </div>

      {/* Notification Preferences Section */}
      {accountNotificationSettings && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/20 to-green-600/10 dark:from-green-400/20 dark:to-green-500/10 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-green-600 dark:text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {displayText[DisplayTextKeys.NOTIFICATION_PREFERENCES_TEXT]}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage email notification settings</p>
            </div>
          </div>
          <DisplayForm
            titleContainerClassName="hidden"
            sectionsContainerClassName=""
            sections={notificationPreferenceSection}
            model={{ ...model, modelType: displayText[DisplayTextKeys.NOTIFICATION_PREFERENCES_TEXT] }}
          />
        </div>
      )}
    </div>
  );
};

export default Settings;
